import { Control, UseFormRegister } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import { JobFormData } from './JobCreate'; // Adjust path if needed

interface JobRequirementsFormProps {
  control: Control<JobFormData>;
  register: UseFormRegister<JobFormData>;
  errors?: any;
  maxSalaryDisplay: string;
  onMaxSalaryChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxSalaryBlur: () => void;
}

const JobRequirementsForm: React.FC<JobRequirementsFormProps> = ({
  control,
  register,
  errors,
  maxSalaryDisplay,
  onMaxSalaryChange,
  onMaxSalaryBlur,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'requirements.requiredQualifications',
  });

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-txt-primary">Job Requirements</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-txt-primary font-medium mb-1">Required Qualifications</label>
          {fields.map((field, index) => (
            <div key={field.id} className="flex space-x-2 mb-2">
              <input
                {...register(`requirements.requiredQualifications.${index}.value` as const)}
                placeholder="e.g., Bachelor's in Computer Science"
                className="flex-1 p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500 max-w-md"
              />
              <button type="button" onClick={() => remove(index)} className="px-2 py-1 bg-red-500 text-txt-primary rounded hover:bg-red-600">
                Remove
              </button>
            </div>
          ))}
          <button type="button" onClick={() => append({ value: '' })} className="mt-2 px-4 py-2 bg-brand-600 text-txt-primary rounded hover:bg-brand-700">
            Add Qualification
          </button>
          {errors?.requiredQualifications && <p className="text-red-500 text-sm mt-1">{errors.requiredQualifications.message}</p>}
        </div>

        <div>
          <label className="block text-txt-primary font-medium mb-1">Minimum Experience Years</label>
          <input
            type="number"
            {...register('requirements.minExperienceYears', { valueAsNumber: true })}
            placeholder="Minimum Experience Years"
            className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500 max-w-md"
          />
          {errors?.minExperienceYears && <p className="text-red-500 text-sm mt-1">{errors.minExperienceYears.message}</p>}
        </div>

        <div>
          <label className="block text-txt-primary font-medium mb-1">Maximum annual CTC (optional)</label>
          <input
            type="text"
            value={maxSalaryDisplay}
            onChange={onMaxSalaryChange}
            onBlur={onMaxSalaryBlur}
            placeholder="Maximum annual CTC (optional)"
            className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500 max-w-md"
          />
          {errors?.maxSalary && <p className="text-red-500 text-sm mt-1">{errors.maxSalary.message}</p>}
        </div>

        <div>
          <label className="block text-txt-primary font-medium mb-1">Work Location</label>
          <select
            {...register('requirements.workLocation')}
            className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 max-w-md"
          >
            <option value="OFFICE">Office</option>
            <option value="REMOTE">Remote</option>
            <option value="HYBRID">Hybrid</option>
          </select>
          {errors?.workLocation && <p className="text-red-500 text-sm mt-1">{errors.workLocation.message}</p>}
        </div>

        <div>
          <label className="block text-txt-primary font-medium mb-1">Required Location (optional)</label>
          <input
            {...register('requirements.requiredLocation')}
            placeholder="Required Location (optional)"
            className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500 max-w-md"
          />
          {errors?.requiredLocation && <p className="text-red-500 text-sm mt-1">{errors.requiredLocation.message}</p>}
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            {...register('requirements.isEmploymentEquity')}
            className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded"
          />
          <label className="ml-2 text-txt-primary">Employment Equity Position</label>
          {errors?.isEmploymentEquity && <p className="text-red-500 text-sm ml-2">{errors.isEmploymentEquity.message}</p>}
        </div>

        <div>
          <label className="block text-txt-primary font-medium mb-1">Preferred Race (optional)</label>
          <input
            {...register('requirements.equityRace')}
            placeholder="Preferred Race (optional)"
            className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500 max-w-md"
          />
          {errors?.equityRace && <p className="text-red-500 text-sm mt-1">{errors.equityRace.message}</p>}
        </div>

        <div>
          <label className="block text-txt-primary font-medium mb-1">Preferred Gender (optional)</label>
          <input
            {...register('requirements.equityGender')}
            placeholder="Preferred Gender (optional)"
            className="w-full p-3 bg-white text-gray-900 border border-gray-300 rounded-input focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition duration-200 placeholder:text-gray-500 max-w-md"
          />
          {errors?.equityGender && <p className="text-red-500 text-sm mt-1">{errors.equityGender.message}</p>}
        </div>
      </div>

      {/* Citizenship & Work Authorization Section - Full width */}
      <div className="space-y-3">
        <h4 className="text-lg font-semibold text-txt-primary">Citizenship & Work Authorization</h4>
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            {...register('requirements.citizensOnly')}
            className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded mt-1"
          />
          <div className="flex-1">
            <label className="text-txt-primary font-medium">South African Citizens Only</label>
            <p className="text-txt-secondary text-sm mt-1">Check this to restrict applications to South African citizens only. Foreign nationals will not be able to apply.</p>
          </div>
        </div>
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            {...register('requirements.requiresWorkAuth')}
            className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded mt-1"
          />
          <div className="flex-1">
            <label className="text-txt-primary font-medium">Work Authorization Required for Foreign Nationals</label>
            <p className="text-txt-secondary text-sm mt-1">Check this to allow foreign nationals to apply (they must have valid work permits). Citizens can always apply freely.</p>
          </div>
        </div>
        <div className="bg-yellow-100 border-l-4 border-yellow-400 p-3 rounded-r">
          <p className="text-sm text-gray-800">
            <strong>Legal Requirement:</strong> If this position is open to non-citizens, you must require work authorization. This ensures compliance with SA employment law.
          </p>
        </div>
        {errors?.citizensOnly && <p className="text-red-500 text-sm">{errors.citizensOnly.message}</p>}
        {errors?.requiresWorkAuth && <p className="text-red-500 text-sm">{errors.requiresWorkAuth.message}</p>}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          {...register('requirements.requireCv')}
          className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded"
        />
        <label className="ml-2 text-txt-primary">Require CV</label>
        {errors?.requireCv && <p className="text-red-500 text-sm ml-2">{errors.requireCv.message}</p>}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          {...register('requirements.requirePayslip')}
          className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded"
        />
        <label className="ml-2 text-txt-primary">Require Payslip</label>
        {errors?.requirePayslip && <p className="text-red-500 text-sm ml-2">{errors.requirePayslip.message}</p>}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          {...register('requirements.requireTranscript')}
          className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded"
        />
        <label className="ml-2 text-txt-primary">Require Academic Transcript</label>
        {errors?.requireTranscript && <p className="text-red-500 text-sm ml-2">{errors.requireTranscript.message}</p>}
      </div>
    </div>
  );
};

export default JobRequirementsForm;
